import { AnimatePresence, motion } from 'motion/react';
import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

import { Icon } from '~/modules/elements/akui/icon';
import { serverRpc } from '~/modules/remote-mic/network/client';
import { captureAvatar } from '~/modules/user-profiles/avatar-capture';
import { HelpEntry } from '~/routes/keyboard-help/context';
import { usePhoneToast } from '~/routes/remote-mic/components/phone-toast-context';

type Photo = NonNullable<HelpEntry['photo']>;

/**
 * The phone's own camera, offered on a screen that says it wants a picture.
 *
 * Not a mirrored control and deliberately so: pressing it opens the camera in this hand, and there
 * is nothing for the television to do until a picture comes back. The television keeps its own
 * `add-photo`, which drives ITS webcam - so each device offers the camera it actually has rather
 * than a remote shutter for somebody else's. A desktop wired to a television often has no webcam at
 * all, which is the case this exists for.
 */
export function PhonePhoto({ photo }: { photo: Photo }) {
  const [capturing, setCapturing] = useState(false);
  const flash = usePhoneToast();

  const send = (dataUrl: string) => {
    setCapturing(false);
    void serverRpc.profiles
      .setPhoto(dataUrl)
      .then(() => flash('Photo sent to the TV'))
      .catch(() => flash('The TV did not take the photo'));
  };

  return (
    <div
      data-test="phone-photo"
      className="flex shrink-0 items-center gap-3 rounded-[1.125rem] bg-black/50 px-4.5 py-3 shadow-[inset_0_0_0_1px_rgba(255,255,255,0.14)]">
      <span
        aria-hidden
        className="flex size-11 shrink-0 items-center justify-center rounded-full bg-white/10 text-white/55">
        <Icon icon={photo.taken ? 'ic:baseline-check' : 'ic:baseline-person'} size={6} />
      </span>
      <span className="flex min-w-0 flex-1 flex-col">
        <span className="text-[0.8125rem] tracking-[0.08em] text-white/60 uppercase">Photo</span>
        <span className="truncate text-[1.0625rem]" data-test="phone-photo-state">
          {photo.taken ? 'Taken' : 'None yet'}
        </span>
      </span>
      <button
        type="button"
        onClick={() => setCapturing(true)}
        data-test="phone-photo-take"
        className="bg-active flex h-12 shrink-0 cursor-pointer items-center gap-2 rounded-[0.875rem] border-none px-4 text-[1.0625rem] font-bold text-black transition-transform duration-100 active:scale-[0.96]">
        <Icon icon="ic:baseline-photo-camera" size={5} />
        {photo.taken ? 'Retake' : 'Take it here'}
      </button>
      <PhotoCapture open={capturing} subject={photo.subject} onCancel={() => setCapturing(false)} onCapture={send} />
    </div>
  );
}

interface CaptureProps {
  open: boolean;
  subject: string;
  onCancel: () => void;
  onCapture: (dataUrl: string) => void;
}

/**
 * Full bleed, one action, in the join flow's language rather than the mirror's. The circle is where
 * the avatar crops to, drawn so nobody takes a photo that loses its own head.
 *
 * The camera permission is asked for HERE, when the screen opens, and never on arrival - the phone
 * already spends its weakest step asking for the microphone, and a second prompt on top of that one
 * is what loses a join.
 */
function PhotoCapture({ open, subject, onCancel, onCapture }: CaptureProps) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const [front, setFront] = useState(true);
  const [ready, setReady] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setReady(false);
    setError('');

    // `navigator` directly rather than `userMediaService`, for the reason `webcam-capture.tsx`
    // gives: that service tracks MICROPHONE access, and a camera prompt answered here must not be
    // recorded as the mic being granted or refused.
    navigator.mediaDevices
      .getUserMedia({ video: { facingMode: front ? 'user' : 'environment' } })
      .then((stream) => {
        if (cancelled) {
          stream.getTracks().forEach((track) => track.stop());
          return;
        }
        streamRef.current = stream;
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          void videoRef.current.play();
          setReady(true);
        }
      })
      .catch(() => {
        if (!cancelled) setError('This phone would not open its camera.');
      });

    return () => {
      cancelled = true;
      streamRef.current?.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    };
  }, [open, front]);

  const take = () => {
    const video = videoRef.current;
    if (!video) return;

    // Mirrored only for the front camera, which is the one shown flipped - the rear camera is
    // pointed at something that is not the person holding the phone.
    const dataUrl = captureAvatar(video, { mirrored: front });
    if (!dataUrl) return;

    streamRef.current?.getTracks().forEach((track) => track.stop());
    onCapture(dataUrl);
  };

  return createPortal(
    <AnimatePresence>
      {open && (
        <motion.div
          role="dialog"
          aria-modal="true"
          data-test="phone-camera"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.18 }}
          className="fixed inset-0 z-[20004] flex flex-col bg-black"
          style={{ paddingBottom: 'max(1.25rem, env(safe-area-inset-bottom))' }}>
          <div className="flex shrink-0 items-center gap-3 px-5 pt-4 pb-3">
            <button
              type="button"
              onClick={onCancel}
              aria-label="Close"
              data-test="phone-camera-close"
              className="flex size-10 shrink-0 items-center justify-center rounded-xl border-none bg-white/8 text-white">
              <Icon icon="ic:baseline-close" size={5.5} />
            </button>
            <span className="min-w-0 flex-1 truncate text-[1.1875rem] font-bold">
              {subject ? `Photo for ${subject}` : 'Photo'}
            </span>
            <button
              type="button"
              onClick={() => setFront((current) => !current)}
              aria-label="Switch camera"
              data-test="phone-camera-flip"
              className="flex size-10 shrink-0 items-center justify-center rounded-xl border-none bg-white/8 text-white">
              <Icon icon="ic:baseline-flip-camera-ios" size={5.5} />
            </button>
          </div>

          <div className="relative flex min-h-0 flex-1 items-center justify-center overflow-hidden">
            <video
              ref={videoRef}
              autoPlay
              playsInline
              muted
              data-test="phone-camera-preview"
              className={`absolute inset-0 size-full object-cover ${front ? 'scale-x-[-1]' : ''}`}
            />
            {/* The crop, drawn rather than described: everything outside the circle is thrown away by
                `captureAvatar`'s centre square, so this is the only warning anybody gets. */}
            <span
              aria-hidden
              className="pointer-events-none relative size-[min(72vw,20rem)] rounded-full shadow-[0_0_0_100vmax_rgba(0,0,0,0.6),inset_0_0_0_2px_rgba(255,255,255,0.85)]"
            />
            <span className="pointer-events-none absolute right-0 bottom-6 left-0 text-center text-[1rem] text-white/85">
              {error || (ready ? 'Fit your face in the circle' : 'Starting camera…')}
            </span>
          </div>

          <div className="flex shrink-0 items-center justify-center pt-5">
            <button
              type="button"
              onClick={take}
              disabled={!ready}
              aria-label="Take the photo"
              data-test="phone-camera-shutter"
              className="flex size-[4.5rem] cursor-pointer items-center justify-center rounded-full border-none bg-white text-black shadow-[0_0_0_0.25rem_rgba(255,255,255,0.3)] transition-transform duration-100 active:scale-[0.94] disabled:opacity-40">
              <Icon icon="ic:baseline-photo-camera" size={8} />
            </button>
          </div>
        </motion.div>
      )}
    </AnimatePresence>,
    document.body,
  );
}
